/**
 * The revocation registry for everything the Journal derives from user content.
 *
 * An attachment thumbnail is not stored as an `<img src>` the app can simply
 * stop rendering: it is an object URL, and an object URL outlives the element
 * that referenced it. Left alone it stays resolvable for the life of the
 * document — a second tab, a devtools panel, or a still-mounted detached node
 * can keep painting the photo long after the curtain went up. So every
 * journal-derived preview is registered here and revoked in one synchronous
 * sweep the moment the page is concealed.
 *
 * **Synchronously, in the lifecycle handler** is the load-bearing part.
 * `visibilitychange` and `pagehide` are the last moments the page runs before
 * the browser may capture it, and a revocation deferred to a React effect
 * happens a render later — after the frame that gets photographed. This class
 * is imperative for that reason and is called from the event handler, not from
 * a `useEffect`.
 *
 * What this does **not** buy, stated plainly because the privacy copy has to
 * match (`02-ARCHITECTURE.md` §6.6):
 *
 * - Pixels already painted are gone from our control. Mission Control, Alt-Tab,
 *   iOS's app switcher and Android's recents all snapshot at a moment no page
 *   can observe or pre-empt. The curtain and this sweep cover `visibilitychange`
 *   and `blur`; the OS-level snapshot is a documented residual gap.
 * - An attacker with the unlocked device and the tab open gets everything. The
 *   library is in memory by definition while the Journal is revealed, and so is
 *   the passphrase-derived key. These are foreground-lifetime mitigations only.
 * - Nothing here touches bytes at rest. Revoking a URL does not delete the
 *   attachment; OPFS and IndexedDB are readable from devtools or by copying the
 *   browser profile whenever no passphrase is enrolled.
 */

import type { Cancel } from "@ff/domain/ports";

export interface PreviewObjectUrls {
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
}

/**
 * jsdom has no `URL.createObjectURL`, and neither does a worker in some
 * browsers, so the two functions are injected rather than reached for. The
 * default is the real one; a test supplies a recorder.
 */
export function browserPreviewObjectUrls(): PreviewObjectUrls {
  return {
    createObjectURL: (blob) => URL.createObjectURL(blob),
    revokeObjectURL: (url) => {
      URL.revokeObjectURL(url);
    },
  };
}

export class JournalPreviewVault {
  readonly #urls: PreviewObjectUrls;
  readonly #issued = new Set<string>();
  readonly #revocables = new Set<() => void>();

  constructor(urls: PreviewObjectUrls = browserPreviewObjectUrls()) {
    this.#urls = urls;
  }

  /**
   * Mints a preview URL that the next concealment will revoke.
   *
   * There is no un-tracked variant on purpose: a caller that wanted a URL
   * outside the registry would be creating exactly the reference this class
   * exists to prevent, and making them delete a line to do it is the point.
   */
  createObjectURL(blob: Blob): string {
    const url = this.#urls.createObjectURL(blob);
    this.#issued.add(url);
    return url;
  }

  /**
   * Registers something that is not a URL — a decoded bitmap, a media element's
   * source, a rendered excerpt held in a mutable ref. Returns a cancel so a
   * component that unmounts normally can withdraw it rather than leaving a
   * closure over a dead node to be called at conceal time.
   */
  retain(revoke: () => void): Cancel {
    this.#revocables.add(revoke);
    return () => {
      this.#revocables.delete(revoke);
    };
  }

  /** Releases one preview early. Revoking twice is defined and harmless. */
  revoke(url: string): void {
    if (!this.#issued.delete(url)) return;
    this.#urls.revokeObjectURL(url);
  }

  /**
   * The concealment sweep. Never throws: it runs on the last line the page
   * executes before it may be captured, and one badly behaved revocable must
   * not stop the rest from being released.
   */
  revokeAll(): void {
    for (const url of [...this.#issued]) {
      this.#issued.delete(url);
      try {
        this.#urls.revokeObjectURL(url);
      } catch {
        // Nothing to do and nowhere to report it: the URL is being abandoned
        // either way, and the remaining previews still have to be released.
      }
    }
    for (const revoke of [...this.#revocables]) {
      this.#revocables.delete(revoke);
      try {
        revoke();
      } catch {
        // As above.
      }
    }
  }

  /** How many previews are outstanding. Diagnostics and tests. */
  size(): number {
    return this.#issued.size + this.#revocables.size;
  }
}
